import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { UserPlus, Eye, EyeOff, AlertCircle, GitBranch, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

const inputStyle = { backgroundColor: 'rgba(20, 27, 80, 0.6)', border: '1px solid rgba(251, 221, 104, 0.2)' };
const goldBtn = { backgroundColor: '#fbdd68', color: '#000e2a', boxShadow: '0 0 15px rgba(251, 221, 104, 0.2)' };
const ghostBtn = { backgroundColor: 'rgba(20, 27, 80, 0.6)', border: '1px solid rgba(251, 221, 104, 0.3)', color: '#fbdd68' };

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', name: '', license_key: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // 'form' -> normal sign-up; existing email -> 'choice' -> 'confirm' -> 'waiting' (or 'kept')
  const [step, setStep] = useState('form');
  const [preview, setPreview] = useState(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 10) { setError('Password must be at least 10 characters'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, form, { withCredentials: true });
      if (data && data.status === 'existing_account') {
        // The email already has an account: prove the password, then offer keep / branch off.
        const prev = await axios.post(`${API}/auth/register/preview`,
          { email: form.email, password: form.password, license_key: form.license_key },
          { withCredentials: true });
        setPreview(prev.data);
        setStep('choice');
      } else {
        setUser(data);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || 'Could not create your account');
    } finally {
      setLoading(false);
    }
  };

  const handleBranchOff = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register/branch-off`,
        { email: form.email, password: form.password, license_key: form.license_key, confirm: true },
        { withCredentials: true });
      setStep('waiting');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const orgName = preview?.current?.org_name || 'your current organization';
  const role = preview?.current?.role || 'member';

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: '#000e2a' }}>
      <div className="relative z-10 w-full max-w-md mx-4 animate-slide-up" data-testid="register-page">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/hat-logo.png" alt="BIG Hat" className="w-20 h-20 mx-auto mb-3 object-contain" />
          <h1 className="text-3xl font-bold" style={{ color: '#fbdd68', fontFamily: "'Lemonada', cursive" }}>
            BIG Hat
          </h1>
          <p className="mt-2 text-sm" style={{ color: '#8892b0' }}>Command Center</p>
        </div>

        <div className="glass-card rounded-2xl p-8" data-testid="register-form">
          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm flex items-center gap-2"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444' }}
              data-testid="register-error">
              <AlertCircle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}

          {step === 'form' && (
            <>
              <h2 className="text-lg font-semibold text-white mb-6 text-center">Create your account</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#8892b0' }}>License Key</label>
                  <input data-testid="register-license-key-input" value={form.license_key} onChange={update('license_key')}
                    className="w-full px-4 py-3 rounded-lg text-sm text-white outline-none transition-all duration-300"
                    style={inputStyle} placeholder="BHE-XXXX-XXXX-XXXX-XXXX" required />
                  <p className="text-[10px] mt-1" style={{ color: '#8892b0' }}>From your BIG Hat Entertainment purchase</p>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#8892b0' }}>Full Name</label>
                  <input data-testid="register-name-input" value={form.name} onChange={update('name')}
                    className="w-full px-4 py-3 rounded-lg text-sm text-white outline-none transition-all duration-300"
                    style={inputStyle} placeholder="Your name" required />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#8892b0' }}>Email</label>
                  <input data-testid="register-email-input" type="email" value={form.email} onChange={update('email')}
                    className="w-full px-4 py-3 rounded-lg text-sm text-white outline-none transition-all duration-300"
                    style={inputStyle} placeholder="your@email.com" required />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#8892b0' }}>Password</label>
                  <div className="relative">
                    <input data-testid="register-password-input" type={showPass ? 'text' : 'password'} value={form.password} onChange={update('password')}
                      className="w-full px-4 py-3 rounded-lg text-sm text-white outline-none transition-all duration-300 pr-12"
                      style={inputStyle} placeholder="Min 10 characters" required />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity">
                      {showPass ? <EyeOff size={18} color="#8892b0" /> : <Eye size={18} color="#8892b0" />}
                    </button>
                  </div>
                </div>

                <button data-testid="register-submit-button" type="submit" disabled={loading}
                  className="w-full py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  style={goldBtn}>
                  <UserPlus size={16} />
                  {loading ? 'Checking...' : 'Create Account'}
                </button>
              </form>
            </>
          )}

          {step === 'choice' && (
            <div className="space-y-5" data-testid="branch-off-choice">
              <h2 className="text-lg font-semibold text-white text-center">You already have an account</h2>
              <p className="text-sm text-center" style={{ color: '#c8d0e8' }}>
                Your account is part of <span className="font-semibold" style={{ color: '#fbdd68' }}>{orgName}</span> as{' '}
                <span className="font-semibold" style={{ color: '#fbdd68' }}>{role === 'account_owner' ? 'owner' : role}</span>.
                Do you want to keep your account there, or branch off and create your own organization?
              </p>
              <button data-testid="keep-account-button" onClick={() => setStep('kept')} disabled={loading}
                className="w-full py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                style={goldBtn}>
                Keep my account where it is
              </button>
              <button data-testid="branch-off-button" onClick={() => { setError(''); setStep('confirm'); }} disabled={loading}
                className="w-full py-3 rounded-lg font-semibold text-sm transition-all duration-300 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={ghostBtn}>
                <GitBranch size={16} /> Branch off and create my own organization
              </button>
            </div>
          )}

          {step === 'kept' && (
            <div className="space-y-5 text-center" data-testid="branch-off-kept">
              <CheckCircle className="w-10 h-10 mx-auto" style={{ color: '#4ade80' }} />
              <h2 className="text-lg font-semibold text-white">Nothing has changed</h2>
              <p className="text-sm" style={{ color: '#c8d0e8' }}>
                Your account stays in <span className="font-semibold" style={{ color: '#fbdd68' }}>{orgName}</span> exactly as it is.
                You can sign in as usual.
              </p>
              <Link data-testid="kept-signin-link" to="/login"
                className="block w-full py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:shadow-lg"
                style={goldBtn}>
                Go to Sign In
              </Link>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-5" data-testid="branch-off-confirm">
              <h2 className="text-lg font-semibold text-white text-center">Create your own organization</h2>
              <div className="p-4 rounded-lg text-sm space-y-3" style={{ backgroundColor: 'rgba(20, 27, 80, 0.6)', border: '1px solid rgba(251, 221, 104, 0.2)', color: '#c8d0e8' }}>
                <p>An Owner's account is for control of your own events and the hosts under you.</p>
                <p>BIG Hat approves every transfer. Until then you stay in{' '}
                  <span className="font-semibold" style={{ color: '#fbdd68' }}>{orgName}</span> exactly as now.</p>
                <p style={{ color: '#8892b0' }}>License to be used: {preview?.license_masked}</p>
              </div>
              <button data-testid="branch-off-confirm-button" onClick={handleBranchOff} disabled={loading}
                className="w-full py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                style={goldBtn}>
                {loading ? 'Sending request...' : 'Confirm — request my own organization'}
              </button>
              <button data-testid="branch-off-back-button" onClick={() => setStep('choice')} disabled={loading}
                className="w-full py-2 rounded-lg text-sm transition-all duration-300 hover:opacity-80 disabled:opacity-50"
                style={{ color: '#8892b0' }}>
                Go back
              </button>
            </div>
          )}

          {step === 'waiting' && (
            <div className="space-y-5 text-center" data-testid="branch-off-waiting">
              <Clock className="w-10 h-10 mx-auto animate-pulse" style={{ color: '#fbdd68' }} />
              <h2 className="text-lg font-semibold text-white">Waiting for approval</h2>
              <p className="text-sm" style={{ color: '#c8d0e8' }}>
                Your request was sent to BIG Hat. Until it is approved, nothing changes: you stay in{' '}
                <span className="font-semibold" style={{ color: '#fbdd68' }}>{orgName}</span> with the same sign-in.
              </p>
              <p className="text-sm" style={{ color: '#8892b0' }}>
                Once approved, remember to link your Program to your new account from Settings.
              </p>
              <Link data-testid="waiting-signin-link" to="/login"
                className="block w-full py-3 rounded-lg font-bold text-sm transition-all duration-300 hover:shadow-lg"
                style={goldBtn}>
                Go to Sign In
              </Link>
            </div>
          )}
        </div>

        {step === 'form' && (
          <p className="text-center text-xs mt-6" style={{ color: '#8892b0' }}>
            Already have an account?{' '}
            <Link data-testid="login-link" to="/login" className="font-semibold hover:opacity-80 transition-opacity" style={{ color: '#fbdd68' }}>
              Sign In
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
